/**
 * Матрица «роль → набор прав».
 *
 * Соответствует таблице PERMISSIONS.md §4. Расхождение ловится тестом
 * `matrix.spec.ts`.
 *
 * Наличие права в наборе НЕ означает доступ: сверх него движок всегда проверяет
 * scope (есть ли доступ к конкретному аккаунту) и уровень
 * (min(потолок роли, уровень назначения) ≥ требуемого).
 */

import { PERMISSIONS, type Permission } from './permissions.js';
import { ROLE_TIER, type Role, type RoleTier } from './types.js';

/** Стажёр: только чтение. Ни одного write-права, ни identity аккаунта. */
const INTERN_GRANTS: readonly Permission[] = [
  'telegram.account.view',
  'telegram.chat.view',
  'telegram.message.view',
];

/**
 * Сотрудник: чтение + переписка в пределах назначенных аккаунтов.
 *
 * Ступень общая для менеджера, оператора и HR.
 */
const MANAGER_GRANTS: readonly Permission[] = [
  ...INTERN_GRANTS,
  'telegram.account.identity.view',
  'telegram.chat.metadata.update',
  'telegram.message.send',
  'telegram.message.reply',
  'telegram.message.edit',
  // Стажёру список быстрых ответов не выдаём: отправлять он не может,
  // а лишний элемент интерфейса только сбивал бы с толку.
  'quickreply.view',
  // Апартаменты: подбор и бронь — работа менеджера. Стажёру не выдаём:
  // бронь — это деньги компании.
  'stays.view',
  'stays.book',
];

/**
 * Старший: всё что сотрудник + управление доступом ВНУТРИ своего владения.
 * Ступень общая для старшего менеджера и старшего оператора. Удаления нет — на MVP оно только у ADMIN (§27 ТЗ).
 *
 * `user.view` выдан, потому что без списка кандидатов Senior не сможет
 * назначать. Ограничение — в проекции сериализатора: только MANAGER/INTERN,
 * только id/displayName/role/status. См. PERMISSIONS.md §4, сноска 1.
 */
const SENIOR_MANAGER_GRANTS: readonly Permission[] = [
  ...MANAGER_GRANTS,
  'telegram.account.update',
  'telegram.chat.assign',
  'quickreply.manage',
  'folder.manage',
  'account.assign.manager',
  'account.assign.intern',
  'account.unassign',
  'user.view',
  'audit.view.scoped',
  // «Проверенная» и ЧС — решение для всей компании, его принимают старшие.
  'stays.property.moderate',
];

/** Админ: все права без исключений. */
const ADMIN_GRANTS: readonly Permission[] = [...PERMISSIONS];

/**
 * Права по СТУПЕНИ, а не по роли.
 *
 * Оператор и менеджер отличаются командой, а не набором кнопок (см. RoleTier),
 * поэтому набор описывается один раз на ступень. Выписывать его для каждой из
 * восьми ролей значило бы завести три копии одного списка, которые рано или
 * поздно разойдутся — и разойдутся молча, потому что тест на них смотрит
 * порознь.
 */
const TIER_PERMISSIONS: Readonly<Record<RoleTier, ReadonlySet<Permission>>> = {
  INTERN: new Set(INTERN_GRANTS),
  MEMBER: new Set(MANAGER_GRANTS),
  SENIOR: new Set(SENIOR_MANAGER_GRANTS),
  ADMIN: new Set(ADMIN_GRANTS),
};

export const ROLE_PERMISSIONS: Readonly<Record<Role, ReadonlySet<Permission>>> = Object.freeze(
  Object.fromEntries(
    (Object.keys(ROLE_TIER) as Role[]).map((role) => [role, TIER_PERMISSIONS[ROLE_TIER[role]]]),
  ) as Record<Role, ReadonlySet<Permission>>,
);

export function roleHasPermission(role: Role, permission: Permission): boolean {
  return ROLE_PERMISSIONS[role].has(permission);
}

/** Плоский список пар для сида таблицы RolePermission. */
export function flattenRolePermissions(): Array<{ role: Role; permissionKey: Permission }> {
  const out: Array<{ role: Role; permissionKey: Permission }> = [];
  for (const role of Object.keys(ROLE_PERMISSIONS) as Role[]) {
    for (const permissionKey of ROLE_PERMISSIONS[role]) {
      out.push({ role, permissionKey });
    }
  }
  return out;
}
